// useQueueStream — the v2 realtime queue layer.
//
// Replaces the v1 polling loop. It:
//   1. Seeds the screen from the /day snapshot (per visible doctor mapping).
//   2. Opens a single per-hospital EventSource and applies booking_event /
//      session_event / resync messages.
//   3. Re-seeds on every (re)connect so a TV that runs for days never shows
//      a stale screen after a dropout. EventSource auto-reconnects (~3s).
//
// The stream is whole-hospital; we filter client-side to the mappings this
// screen shows.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getDoctorDaySnapshot } from "../api/snapshot";
import { hitRefreshToken } from "../../lib/apis/user";
import { setAccessTokenForInterceptor } from "../../lib/utils/axiosUtil";
import { baseURLV2, VISIBLE_STATUS_IDS } from "../constants";
import {
  BookingV2,
  ConnectionStatus,
  DaySnapshot,
  DocDelay,
  QueueStreamMessage,
  SessionMeta,
} from "../types";

interface UseQueueStreamArgs {
  hospitalId?: string;
  token?: string;
  mappingIds: string[];
  date: string; // YYYY-MM-DD
  // Called with a fresh access token after a successful refresh, so the
  // parent can update UserContext (and the SSE connection reconnects with it).
  onToken?: (token: string) => void;
}

interface UseQueueStreamResult {
  // doctor mapping id -> that doctor's visible bookings (exactly what their
  // /day snapshot returned, kept live by SSE).
  bookingsByMapping: Record<string, BookingV2[]>;
  delaysByAvailability: Record<string, DocDelay>;
  sessionsByAvailability: Record<string, SessionMeta>;
  status: ConnectionStatus;
  lastError: string | null;
  // True once a /day snapshot has been applied at least once. Callers use it
  // to tell "no patients" apart from "haven't loaded yet" — both look like an
  // empty bookingsByMapping, but only the first should change what's on screen.
  seeded: boolean;
}

// How often the heartbeat checks the stream's health, and how long the stream
// may show no sign of life (no open, no message) before we force a rebuild.
// The server idle-drops a quiet SSE stream at ~20s, so 30s of silence on a
// non-OPEN stream means the browser's own auto-reconnect is stuck (throttled
// while backgrounded, or wedged after sleep) and we need to rebuild it.
const HEARTBEAT_MS = 10000;
const STALE_RECONNECT_MS = 30000;

const toNum = (v: any): number | undefined => {
  if (v == null) return undefined;
  if (typeof v === "number") return v;
  const n = parseInt(String(v), 10);
  return Number.isNaN(n) ? undefined : n;
};

// Normalize a raw /day or SSE booking into the shape the UI reads, tolerating
// field-name differences and stamping the doctor mapping + availability when
// the row itself doesn't carry them (snapshot session bookings often don't —
// the mapping is implied by which /day we fetched).
const normalizeBooking = (
  raw: any,
  mappingId?: string,
  availabilityId?: string
): BookingV2 => {
  // The 0-9 status code is `status` (INT). `status_id` is a UUID FK to the
  // status table — NOT the code — so we must never read it as the status.
  const status = toNum(raw?.status ?? raw?.booking_status);
  return {
    ...raw,
    booking_id: raw?.booking_id ?? raw?.id,
    token_number: raw?.token_number ?? raw?.token,
    status,
    status_id: status, // overwrite the UUID so downstream `status_id ?? status` is safe
    // `current_doctor_mapping_id` is only set when a booking is transferred;
    // for a normal booking it's null and the patient belongs to their PRIMARY
    // doctor. Falling back to primary_doctor_mapping_id is essential for SSE
    // booking_event routing (where we have no fetched mappingId to stamp).
    current_doctor_mapping_id:
      raw?.current_doctor_mapping_id ??
      raw?.doctor_mapping_id ??
      raw?.primary_doctor_mapping_id ??
      mappingId,
    availability_id: raw?.availability_id ?? availabilityId,
    full_name:
      raw?.full_name ??
      raw?.patient_full_name ??
      raw?.booking_alias ??
      raw?.patient_name ??
      raw?.name,
  };
};

const statusOf = (b: BookingV2): number | undefined =>
  b.status_id ?? b.status;

const isVisible = (b: BookingV2): boolean => {
  const s = statusOf(b);
  return s != null && VISIBLE_STATUS_IDS.includes(s);
};

export function useQueueStream({
  hospitalId,
  token,
  mappingIds,
  date,
  onToken,
}: UseQueueStreamArgs): UseQueueStreamResult {
  // Latest onToken without re-creating reseed each render.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;
  // mapping id -> that doctor's bookings (keyed by the mapping we fetched).
  const [bookingsByMapping, setBookingsByMapping] = useState<
    Record<string, BookingV2[]>
  >({});
  const [delaysByAvailability, setDelays] = useState<Record<string, DocDelay>>(
    {}
  );
  const [sessionsByAvailability, setSessions] = useState<
    Record<string, SessionMeta>
  >({});
  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const [lastError, setLastError] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);

  // Stable identity for the mapping set so effects don't re-run every render.
  const mappingKey = useMemo(
    () => [...mappingIds].sort().join(","),
    [mappingIds]
  );

  // Keep the latest mapping set readable from inside event handlers without
  // re-subscribing the EventSource.
  const mappingSetRef = useRef<Set<string>>(new Set(mappingIds));
  useEffect(() => {
    mappingSetRef.current = new Set(mappingKey ? mappingKey.split(",") : []);
  }, [mappingKey]);

  // Refetch the per-mapping snapshots and rebuild state. Each mapping's /day
  // call is the source of truth for that doctor's patients — we key bookings
  // by the mapping id we called, never by a field inside the row. Used on
  // first connect and on every reconnect/resync to heal missed events.
  const reseed = useCallback(async (reason: string = "initial") => {
    if (!mappingKey) {
      setBookingsByMapping({});
      setDelays({});
      setSessions({});
      setSeeded(true);
      return;
    }
    const ids = mappingKey.split(",");
    console.log(
      `%c[queue-v2] GET /day ×${ids.length}  (reason: ${reason})`,
      "color:#0B74FF;font-weight:bold"
    );
    const loadAll = () =>
      Promise.all(ids.map((id) => getDoctorDaySnapshot(id, date)));

    let snapshots: DaySnapshot[];
    try {
      snapshots = await loadAll();
    } catch (err: any) {
      const code = err?.response?.status;
      // 401/403 almost always means the access token expired (it was scoped
      // fine moments ago). Refresh once and retry, like the v1 contexts do —
      // do NOT immediately cry "token not scoped".
      if (code === 401 || code === 403) {
        try {
          const r = await hitRefreshToken();
          const fresh = r?.status === 200 ? r.data?.result?.access_token : null;
          if (!fresh) {
            setLastError("Session expired — please sign in again.");
            return;
          }
          setAccessTokenForInterceptor(fresh);
          onTokenRef.current?.(fresh); // bubble up so SSE reconnects fresh
          snapshots = await loadAll(); // retry once with the new token
        } catch (retryErr: any) {
          const rc = retryErr?.response?.status;
          setLastError(
            rc === 401 || rc === 403
              ? "Live queue unavailable — this TV's account isn't permitted for this hospital."
              : "Couldn't load the queue. Retrying shortly…"
          );
          console.error("[queue-v2] snapshot retry failed:", retryErr);
          return;
        }
      } else {
        setLastError("Couldn't load the queue. Retrying shortly…");
        console.error("[queue-v2] snapshot reseed failed:", err);
        return;
      }
    }

    try {
      const nextByMapping: Record<string, BookingV2[]> = {};
      const nextDelays: Record<string, DocDelay> = {};
      const nextSessions: Record<string, SessionMeta> = {};
      // One-time visibility into the real payload shape for debugging.
      const sample = snapshots[0]?.sessions?.[0]?.bookings?.[0];
      if (sample) console.log("[queue-v2] sample /day booking:", sample);

      ids.forEach((mappingId, i) => {
        const snap = snapshots[i];
        const arr: BookingV2[] = [];
        for (const session of snap.sessions) {
          nextSessions[session.availability_id] = {
            availability_id: session.availability_id,
            mapping_id: mappingId,
            start_time: session.start_time,
            end_time: session.end_time,
            doc_delay: session.doc_delay,
          };
          if (session.doc_delay) {
            nextDelays[session.availability_id] = session.doc_delay;
          }
          // Snapshot rows are already scoped to this mapping + date by the
          // query, so only the status filter applies. Skip patients who've
          // been transferred OUT to another doctor — they appear under the
          // doctor they moved to (via that doctor's transferred_in).
          for (const raw of session.bookings ?? []) {
            if (raw?.relation === "transferred_out") continue;
            const b = normalizeBooking(raw, mappingId, session.availability_id);
            if (b.booking_id && isVisible(b)) arr.push(b);
          }
        }
        for (const raw of snap.transferred_in ?? []) {
          const b = normalizeBooking(raw, mappingId, raw?.availability_id);
          if (b.booking_id && isVisible(b)) arr.push(b);
        }
        nextByMapping[mappingId] = arr;
      });
      setBookingsByMapping(nextByMapping);
      setDelays(nextDelays);
      setSessions(nextSessions);
      setLastError(null);
      setSeeded(true);
    } catch (err: any) {
      // Fetch/auth already handled above; this only catches unexpected
      // shape/parsing issues while building state.
      console.error("[queue-v2] failed building queue state:", err);
    }
  }, [mappingKey, date]);

  // Apply a single SSE message to local state.
  const applyMessage = useCallback(
    (msg: QueueStreamMessage) => {
      console.log("[queue-v2] SSE event:", msg?.type, msg);
      if (msg.type === "booking_event") {
        const b = normalizeBooking(msg.booking);
        if (!b?.booking_id) return;
        const target = b.current_doctor_mapping_id;
        const onThisDate =
          !b.booked_date || String(b.booked_date).slice(0, 10) === date;
        setBookingsByMapping((prev) => {
          // Remove this booking from wherever it was (handles transfers and
          // status changes that move it off-screen).
          const next: Record<string, BookingV2[]> = {};
          for (const m of Object.keys(prev)) {
            next[m] = prev[m].filter((x) => x.booking_id !== b.booking_id);
          }
          // Add to the target doctor if it's one we display and still visible.
          if (
            target &&
            mappingSetRef.current.has(target) &&
            isVisible(b) &&
            onThisDate
          ) {
            next[target] = [...(next[target] ?? []), b];
          }
          return next;
        });
      } else if (msg.type === "session_event") {
        setDelays((prev) => ({
          ...prev,
          [msg.availability_id]: msg.doc_delay,
        }));
        // Keep session metadata's delay in sync (and register a session we
        // haven't seen in a snapshot yet).
        setSessions((prev) => {
          const existing = prev[msg.availability_id];
          return {
            ...prev,
            [msg.availability_id]: {
              availability_id: msg.availability_id,
              mapping_id: msg.mapping_id ?? existing?.mapping_id ?? "",
              start_time: existing?.start_time,
              end_time: existing?.end_time,
              doc_delay: msg.doc_delay,
            },
          };
        });
      } else if (msg.type === "resync") {
        debouncedReseed("resync event");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date]
  );

  // Coalesce bursty resync triggers into a single /day refetch (~500ms).
  const reseedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedReseed = useCallback(
    (reason: string = "resync") => {
      if (reseedTimer.current) clearTimeout(reseedTimer.current);
      reseedTimer.current = setTimeout(() => reseed(reason), 500);
    },
    [reseed]
  );

  // Read the latest event handler / token from refs so a token refresh (or a
  // new applyMessage closure) never tears down the live stream.
  const applyRef = useRef(applyMessage);
  applyRef.current = applyMessage;
  const tokenRef = useRef(token);
  tokenRef.current = token;

  // --- Load order: GET /day FIRST (await), THEN open the stream ------------
  // Re-runs ONLY when the doctor set / hospital / date changes — never on a
  // token refresh. Catch-up is the `resync` event (debounced) plus the
  // revive() path below (refocus / network-back / heartbeat).
  //
  // Why revive() exists: EventSource only auto-reconnects reliably while the
  // tab is foregrounded. When a tab is backgrounded or the machine sleeps,
  // Chrome throttles/suspends the timers that drive reconnect; the server
  // idle-drops the stream (~20s), `onerror` fires once, and the built-in
  // reconnect never wakes up — leaving the screen dead on "reconnecting"
  // forever. So we extract connect() and re-invoke it on visibility/online/
  // heartbeat. v1 polling never had this problem because every tick refetched.
  useEffect(() => {
    if (!hospitalId) {
      setStatus("error");
      setLastError("Missing hospital for the live stream.");
      return;
    }

    let cancelled = false;
    let es: EventSource | null = null;
    let reviving = false;
    // Wall-clock of the last sign of life (open or message). The heartbeat
    // uses it to tell a healthy-but-quiet stream from a dead/throttled one.
    let lastActivityAt = Date.now();

    // (Re)open the EventSource. Token is read from the ref at connect time, so
    // a token refresh never tears the stream down. NO /day refetch here.
    const connect = () => {
      if (cancelled || !mappingKey) return;
      const t = tokenRef.current || "";
      try {
        es = new EventSource(
          `${baseURLV2}/events?hospital_id=${encodeURIComponent(
            hospitalId
          )}&token=${encodeURIComponent(t)}`
        );
      } catch (err) {
        console.error("[queue-v2] failed to open EventSource:", err);
        if (!cancelled) setStatus("error");
        return;
      }
      lastActivityAt = Date.now();
      es.onopen = () => {
        lastActivityAt = Date.now();
        if (!cancelled) setStatus("open");
      };
      es.onmessage = (e: MessageEvent) => {
        lastActivityAt = Date.now();
        if (cancelled || !e.data) return; // heartbeats (":") already swallowed
        console.log("[queue-v2] SSE raw e.data:", e.data); // settle resync vs booking_event
        try {
          applyRef.current(JSON.parse(e.data) as QueueStreamMessage);
        } catch (err) {
          console.warn("[queue-v2] unparseable SSE payload:", e.data);
        }
      };
      es.onerror = () => {
        if (!cancelled) setStatus("reconnecting"); // browser MAY auto-reconnect
      };
    };

    // Rebuild a dead/stuck stream. The OPEN guard is the whole trick: a refocus
    // on a live stream is a no-op, so this never turns into accidental polling;
    // only a dead/throttled stream gets torn down and rebuilt. We snapshot
    // FIRST (catch up on anything missed while the stream was down), then
    // re-open — same order as the initial load. The `reviving` flag prevents a
    // double-connect when visibility + online fire together.
    const revive = async (reason: string) => {
      if (cancelled || reviving) return;
      if (es && es.readyState === EventSource.OPEN) return; // healthy → no-op
      reviving = true;
      try {
        console.log(`[queue-v2] reviving SSE (${reason})`);
        if (es) es.close();
        es = null;
        setStatus("connecting");
        await reseed(`revive: ${reason}`); // GET /day — resync missed state
        if (cancelled) return;
        connect(); // then re-open the stream
      } finally {
        reviving = false;
      }
    };

    // Tab refocus / machine wake.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      revive("visibilitychange");
    };
    // Network came back after a blip.
    const onOnline = () => revive("online");

    // Heartbeat for an always-on TV/kiosk that never backgrounds (so
    // visibilitychange never fires) but can still have its stream dropped by a
    // server restart or network blip while foregrounded: if the stream has
    // shown no sign of life for STALE_RECONNECT_MS and isn't OPEN, force a
    // rebuild. The staleness threshold keeps us from tearing down a stream
    // that's merely mid-connect or quiet-but-OPEN.
    const heartbeat = setInterval(() => {
      if (cancelled) return;
      const open = !!es && es.readyState === EventSource.OPEN;
      if (!open && Date.now() - lastActivityAt > STALE_RECONNECT_MS) {
        revive("heartbeat staleness");
      }
    }, HEARTBEAT_MS);

    setStatus("connecting");
    (async () => {
      // Snapshot first — fully await it before opening the stream so early
      // deltas always have state to patch.
      await reseed("initial load (doctors/hospital/date changed)");
      if (cancelled || !mappingKey) return; // navigated away / no doctors
      connect();
    })();

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);

    // Cleanup on unmount / doctor / hospital / date change. Always remove the
    // listeners + interval and close the stream, or we leak one per remount.
    return () => {
      cancelled = true;
      clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      if (es) es.close();
      if (reseedTimer.current) clearTimeout(reseedTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappingKey, hospitalId, date]);

  return {
    bookingsByMapping,
    delaysByAvailability,
    sessionsByAvailability,
    status,
    lastError,
    seeded,
  };
}
